import React from 'react';

interface NoorPieceLogoProps {
  className?: string;
  size?: 'icon' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showSubtext?: boolean;
}

export const NoorPieceLogo: React.FC<NoorPieceLogoProps> = React.memo(({
  className = '',
  size = 'md',
  showSubtext = true,
}) => {
  // Size dimensions map
  const sizeMap = {
    icon: { width: 32, height: 32, isMini: true },
    xs: { width: 36, height: 36, isMini: true },
    sm: { width: 120, height: 120, isMini: false },
    md: { width: 220, height: 220, isMini: false },
    lg: { width: 340, height: 340, isMini: false },
    xl: { width: 460, height: 460, isMini: false },
  };

  const currentSize = sizeMap[size];

  if (currentSize.isMini) {
    return (
      <div
        className={`relative rounded-full bg-[#F8F3EB] text-[#1A1817] flex items-center justify-center select-none overflow-hidden border-2 border-[#C8A45A] shadow-sm shrink-0 ${className}`}
        style={{
          width: `${currentSize.width}px`,
          height: `${currentSize.height}px`,
          boxShadow: '0 2px 10px rgba(200, 164, 90, 0.25)',
        }}
      >
        {/* Outer Ring Accent */}
        <div className="absolute inset-[2px] rounded-full border border-[#C8A45A]/40 pointer-events-none" />

        {/* Mini Lit Candle Icon */}
        <div className="relative flex flex-col items-center justify-center">
          {/* Flame */}
          <div className="w-2.5 h-3.5 relative">
            <svg viewBox="0 0 24 36" className="w-full h-full drop-shadow-[0_0_4px_#FFAA00]">
              <path
                d="M12 0 C 16 10, 22 18, 12 36 C 2 18, 8 10, 12 0 Z"
                fill="url(#miniLogoFlameGrad)"
                className="animate-[flicker_1.8s_ease-in-out_infinite]"
              />
              <path
                d="M12 10 C 14 16, 17 22, 12 34 C 7 22, 10 16, 12 10 Z"
                fill="#FFF4CC"
              />
              <defs>
                <linearGradient id="miniLogoFlameGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFF066" />
                  <stop offset="50%" stopColor="#FF9900" />
                  <stop offset="100%" stopColor="#D44000" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Candle Glass Base */}
          <div className="w-3.5 h-3.5 rounded-b-[2px] border border-[#C8A45A] bg-[#FFFBF5] relative overflow-hidden -mt-0.5">
            <div className="absolute bottom-0 w-full h-[70%] bg-[#F4E8D6]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-full bg-[#F8F3EB] text-[#1A1817] shadow-2xl flex items-center justify-center select-none overflow-hidden border-[3px] border-[#C8A45A]/80 ${className}`}
      style={{
        width: currentSize.width,
        height: currentSize.height,
        boxShadow: '0 10px 40px rgba(200, 164, 90, 0.25), inset 0 0 25px rgba(200, 164, 90, 0.15)',
      }}
    >
      {/* Outer Fine Double Gold Ring */}
      <div className="absolute inset-[6px] rounded-full border border-[#C8A45A]/50 pointer-events-none" />
      <div className="absolute inset-[10px] rounded-full border border-[#D4AF37]/30 pointer-events-none" />

      {/* Side Decorative Gold Accent Dots */}
      <div className="absolute left-3 top-1/2 -translate-y-1/2 flex flex-col space-y-1 opacity-75">
        <span className="w-1 h-1 rounded-full bg-[#C8A45A]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#C8A45A]" />
        <span className="w-1 h-1 rounded-full bg-[#C8A45A]" />
      </div>

      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col space-y-1 opacity-75">
        <span className="w-1 h-1 rounded-full bg-[#C8A45A]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#C8A45A]" />
        <span className="w-1 h-1 rounded-full bg-[#C8A45A]" />
      </div>

      {/* Main Container */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center p-4 w-full h-full">
        
        {/* Botanical Gold Leaf Branch on Left */}
        <svg
          className="absolute left-[12%] top-[22%] w-[26%] h-[32%] text-[#C8A45A] opacity-90 pointer-events-none transform -rotate-12"
          viewBox="0 0 100 120"
          fill="none"
          stroke="currentColor"
        >
          <path
            d="M 50 110 Q 30 70 10 20 M 15 35 Q 25 25 35 30 M 20 50 Q 35 40 45 45 M 28 70 Q 45 60 55 65 M 35 90 Q 55 80 65 85"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          {/* Leaves */}
          <path d="M 10 20 Q 5 10 15 12 Q 22 18 10 20 Z" fill="#C8A45A" />
          <path d="M 35 30 Q 42 18 30 22 Q 25 28 35 30 Z" fill="#221F1E" />
          <path d="M 45 45 Q 58 35 42 38 Q 36 43 45 45 Z" fill="#C8A45A" />
          <path d="M 55 65 Q 68 55 52 58 Q 46 63 55 65 Z" fill="#221F1E" />
        </svg>

        {/* Central Candle Glass Vessel with Lit Flickering Flame */}
        <div className="relative mb-1 flex flex-col items-center">
          {/* Soft Candle Flame Halo Glow */}
          <div className="absolute -top-3 w-8 h-8 rounded-full bg-[#FFB703]/30 blur-md animate-pulse" />
          
          {/* Flame SVG */}
          <div className="relative z-10 w-4 h-6 mb-0.5">
            <svg viewBox="0 0 24 36" className="w-full h-full drop-shadow-[0_0_8px_#FFAA00]">
              <path
                d="M12 0 C 16 10, 22 18, 12 36 C 2 18, 8 10, 12 0 Z"
                fill="url(#logoFlameGrad)"
                className="animate-[flicker_1.8s_ease-in-out_infinite]"
              />
              <path
                d="M12 10 C 14 16, 17 22, 12 34 C 7 22, 10 16, 12 10 Z"
                fill="#FFF4CC"
              />
              <defs>
                <linearGradient id="logoFlameGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFF066" />
                  <stop offset="50%" stopColor="#FF9900" />
                  <stop offset="100%" stopColor="#D44000" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Wick */}
          <div className="w-[2px] h-1.5 bg-[#1a1817] mb-0.5" />

          {/* Glass Candle Vessel */}
          <div className="relative w-11 h-11 rounded-b-md border-2 border-[#C8A45A] bg-white/60 backdrop-blur-sm flex items-center justify-center shadow-inner overflow-hidden">
            {/* Cream Wax Fill */}
            <div className="absolute bottom-0 w-full h-[85%] bg-gradient-to-t from-[#F4E8D6] to-[#FFFBF5] border-t border-[#C8A45A]/30" />
            {/* Reflection Highlight */}
            <div className="absolute top-1 left-1 w-1 h-8 bg-white/80 rounded-full blur-[0.5px]" />
          </div>
        </div>

        {/* Brand Typography */}
        <div className="relative z-10 flex flex-col items-center -mt-1">
          {/* "Noor" with sparkle in the double 'o' */}
          <div className="relative font-serif font-bold tracking-tight text-[#1A1817] flex items-center leading-none text-2xl sm:text-3xl md:text-4xl">
            <span>N</span>
            <span className="relative inline-flex items-center justify-center">
              o
              {/* Sparkle Diamond inside 'o' */}
              <svg className="absolute w-2 h-2 text-[#C8A45A] fill-current animate-pulse" viewBox="0 0 24 24">
                <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" />
              </svg>
            </span>
            <span>o</span>
            <span>r</span>
          </div>

          {/* "Pieces" Script with Heart Flourish */}
          <div
            className="font-serif italic font-normal text-[#C8A45A] -mt-2.5 text-xl sm:text-2xl md:text-3xl tracking-wide flex items-center"
            style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif" }}
          >
            <span>Pieces</span>
            {/* Heart tail flourish */}
            <svg className="w-3.5 h-3.5 ml-1 text-[#C8A45A] fill-current opacity-90 inline-block" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
        </div>

        {/* Subtext divider line & Handmade Candles */}
        {showSubtext && (
          <div className="relative z-10 mt-1 w-full flex flex-col items-center">
            {/* Fine Divider Line with dot */}
            <div className="w-[70%] h-[1px] bg-gradient-to-r from-transparent via-[#C8A45A] to-transparent my-1 relative flex items-center justify-center">
              <span className="w-1 h-1 rounded-full bg-[#C8A45A]" />
            </div>

            <span className="text-[7px] sm:text-[8px] md:text-[9px] font-sans font-medium uppercase tracking-[0.22em] text-[#2A2421]">
              HANDMADE CANDLES
            </span>
            <span className="text-[6px] sm:text-[7px] font-sans font-light uppercase tracking-[0.25em] text-[#7A6E65] mt-0.5">
              MADE WITH LOVE
            </span>

            {/* Bottom Tiny Heart */}
            <svg className="w-2 h-2 mt-0.5 text-[#C8A45A] fill-current" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
});
